import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  listPrograms,
  listProgramCoachCoverage,
  type ProgramStatus,
  type UnifiedProgramResponse,
} from "@/libs/programsApi";

const PLACEHOLDER = "—";

const PROGRAM_TYPE_LABELS: Record<string, string> = {
  group: "Group class",
  private: "Private lesson",
  camp: "Camp",
  clinic: "Clinic",
  league: "League",
  package: "Package",
};

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  published: "Published",
  cancelled: "Cancelled",
  completed: "Completed",
};

const STATUS_COLORS: Record<string, "default" | "error" | "info" | "success"> =
  {
    draft: "default",
    published: "success",
    cancelled: "error",
    completed: "info",
  };

type StatusFilter = ProgramStatus | "all";

const STATUS_FILTERS: ReadonlyArray<{
  value: StatusFilter;
  label: string;
}> = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
];

function formatDate(value: string | null | undefined, timeZone: string): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  });
}

function formatDateRange(
  start: string,
  end: string | null | undefined,
  timeZone: string,
): string {
  const startLabel = formatDate(start, timeZone);
  if (!startLabel) return PLACEHOLDER;
  const endLabel = formatDate(end, timeZone);
  return endLabel ? `${startLabel} – ${endLabel}` : startLabel;
}

function programTypeLabel(programType: string): string {
  return PROGRAM_TYPE_LABELS[programType] ?? humanize(programType);
}

function programStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? humanize(status);
}

function humanize(value: string): string {
  const words = value.replaceAll("_", " ").trim();
  return words
    ? `${words.charAt(0).toUpperCase()}${words.slice(1)}`
    : PLACEHOLDER;
}

function ProgramsLoading({ label }: { label: string }) {
  return (
    <Stack
      role="status"
      aria-live="polite"
      direction="row"
      spacing={1}
      sx={{
        minHeight: 160,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CircularProgress size={20} />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Stack>
  );
}

export function ProgramsList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const compactTable = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [needsCoachOnly, setNeedsCoachOnly] = useState(false);
  const verifiedFacility =
    !facilityLoading &&
    facility !== null &&
    Boolean(facilityId) &&
    facility.facility_id === facilityId
      ? facility
      : null;
  const verifiedFacilityId = verifiedFacility?.facility_id ?? null;
  function openCreate() {
    navigate("/programs/new");
  }

  const programsQuery = useQuery({
    queryKey: ["programs", "list", verifiedFacilityId, statusFilter],
    queryFn: () =>
      listPrograms({
        facility_id: verifiedFacilityId ?? undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
      }),
    enabled: Boolean(verifiedFacilityId),
  });
  const coverageQuery = useQuery({
    queryKey: ["programs", "coach-coverage", verifiedFacilityId],
    queryFn: ({ signal }) =>
      listProgramCoachCoverage(verifiedFacilityId!, signal),
    enabled: Boolean(verifiedFacilityId),
    retry: false,
  });
  const catalogCoverageIds = useMemo(() => new Set(
    (coverageQuery.data?.items ?? [])
      .filter((item) => item.audience === "catalog")
      .map((item) => item.program_id),
  ), [coverageQuery.data?.items]);

  const sortedPrograms = useMemo(
    () =>
      [...(programsQuery.data?.items ?? [])]
        .filter((program) => !needsCoachOnly || catalogCoverageIds.has(program.program_id))
        .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [catalogCoverageIds, needsCoachOnly, programsQuery.data?.items],
  );

  if (facilityLoading) {
    return (
      <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Programs
        </Typography>
        <ProgramsLoading label="Loading programs…" />
      </Stack>
    );
  }

  if (!verifiedFacilityId || !verifiedFacility) {
    return (
      <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Programs
        </Typography>
        <Alert severity="info">Select a facility to view its programs.</Alert>
      </Stack>
    );
  }

  const selectedStatusLabel =
    STATUS_FILTERS.find((option) => option.value === statusFilter)?.label ??
    "selected";
  const hasInitialError =
    programsQuery.isError && programsQuery.data === undefined;
  const hasBackgroundError =
    programsQuery.isError && programsQuery.data !== undefined;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700 }}>
          Programs
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {verifiedFacility.name}
        </Typography>
      </Box>

      {coverageQuery.isError ? (
        <Alert severity="warning">
          Coach coverage could not be loaded. Refresh before assuming every
          upcoming class is assigned.
        </Alert>
      ) : coverageQuery.data?.items.length ? (
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Stack spacing={1.5}>
            <Box>
              <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>
                Coach coverage needs attention
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Assign a coach before these upcoming classes or private sessions take place.
              </Typography>
            </Box>
            <Stack spacing={1}>
              {coverageQuery.data.items.map((item) => (
                <Stack key={item.program_id} direction={{ xs: "column", sm: "row" }}
                  spacing={1} sx={{ alignItems: { sm: "center" }, justifyContent: "space-between",
                    p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      <Typography sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>{item.name}</Typography>
                      <Chip size="small" color="warning" label="Needs coach" />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {item.audience === "private_coaching" ? "Private coaching" : "Class"}
                      {item.starts_at ? ` · ${new Date(item.starts_at).toLocaleString()}` : ""}
                    </Typography>
                  </Box>
                  <Button component={RouterLink} to={`/programs/${item.program_id}`} size="small"
                    variant="outlined" sx={{ alignSelf: { xs: "stretch", sm: "center" } }}>
                    Assign coach
                  </Button>
                </Stack>
              ))}
            </Stack>
            {coverageQuery.data.has_more ? (
              <Alert severity="info">More unassigned work exists. Open each facility’s Programs page to continue.</Alert>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
          <FormControl
            size="small"
            sx={{ width: { xs: "100%", sm: 180 }, flexShrink: 0 }}
          >
          <InputLabel id="program-status-filter-label">Status</InputLabel>
          <Select
            labelId="program-status-filter-label"
            id="program-status-filter"
            value={statusFilter}
            label="Status"
            onChange={(event) =>
              setStatusFilter(event.target.value as StatusFilter)
            }
          >
            {STATUS_FILTERS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          </FormControl>
          <Button size="small" variant={needsCoachOnly ? "contained" : "outlined"}
            onClick={() => setNeedsCoachOnly((value) => !value)}>
            {needsCoachOnly ? "Showing needs coach" : "Needs coach"}
          </Button>
        </Stack>

        <Button
          onClick={openCreate}
          size="small"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
        >
          New program
        </Button>
      </Stack>

      {hasInitialError ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void programsQuery.refetch();
              }}
            >
              Retry
            </Button>
          }
        >
          Programs couldn&apos;t be loaded.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {hasBackgroundError ? (
            <Alert
              severity="warning"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    void programsQuery.refetch();
                  }}
                >
                  Retry
                </Button>
              }
            >
              Programs couldn&apos;t be refreshed.
            </Alert>
          ) : null}

          {!programsQuery.isPending && sortedPrograms.length === 0 ? (
            <Box
              role="status"
              sx={{
                border: 1,
                borderStyle: "dashed",
                borderColor: "divider",
                borderRadius: 1,
                px: 2,
                py: 5,
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                {needsCoachOnly
                  ? "No programs need a coach"
                  : statusFilter === "all"
                  ? "No programs yet"
                  : `No programs match the ${selectedStatusLabel.toLowerCase()} status.`}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {needsCoachOnly
                  ? "Every program in this view has a current coach."
                  : statusFilter === "all"
                  ? "Create your first program to get started."
                  : "Show every program or choose another status."}
              </Typography>
              {needsCoachOnly ? (
                <Button size="small" variant="outlined" sx={{ mt: 2 }}
                  onClick={() => setNeedsCoachOnly(false)}>
                  Show all programs
                </Button>
              ) : statusFilter !== "all" ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => setStatusFilter("all")}
                >
                  Show all programs
                </Button>
              ) : null}
            </Box>
          ) : (
            <TableContainer
              sx={{
                width: "100%",
                overflowX: compactTable ? "visible" : "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Table
                size="small"
                aria-label="Programs"
                aria-busy={programsQuery.isFetching}
                sx={{
                  minWidth: compactTable ? 0 : 720,
                  tableLayout: "fixed",
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        width: compactTable ? "calc(100% - 72px)" : "36%",
                        px: compactTable ? 1.5 : 2,
                      }}
                    >
                      {compactTable ? "Program" : "Name"}
                    </TableCell>
                    {!compactTable ? (
                      <TableCell sx={{ width: "15%" }}>Type</TableCell>
                    ) : null}
                    {!compactTable ? (
                      <TableCell sx={{ width: "16%" }}>Status</TableCell>
                    ) : null}
                    {!compactTable ? (
                      <TableCell sx={{ width: "23%" }}>Dates</TableCell>
                    ) : null}
                    <TableCell
                      aria-label={
                        compactTable ? "Active enrollments" : undefined
                      }
                      align="right"
                      sx={{
                        width: compactTable ? 72 : "10%",
                        px: compactTable ? 1 : 2,
                      }}
                    >
                      {compactTable ? "Active" : "Active enrollments"}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programsQuery.isPending ? (
                    <TableRow>
                      <TableCell colSpan={compactTable ? 2 : 5}>
                        <ProgramsLoading label="Loading programs…" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedPrograms.map((program: UnifiedProgramResponse) => {
                      const typeLabel = programTypeLabel(program.program_type);
                      const statusLabel = programStatusLabel(program.status);
                      const dates = formatDateRange(
                        program.start_date,
                        program.end_date,
                        program.timezone,
                      );

                      return (
                        <TableRow key={program.program_id}>
                          <TableCell
                            sx={{
                              minWidth: 0,
                              overflowWrap: "anywhere",
                              px: compactTable ? 1.5 : 2,
                            }}
                          >
                            <ButtonBase
                              component={RouterLink}
                              to={`/programs/${program.program_id}`}
                              sx={{
                                width: "100%",
                                minHeight: 44,
                                justifyContent: "flex-start",
                                borderRadius: 0.5,
                                color: "inherit",
                                fontWeight: 600,
                                textAlign: "left",
                                overflowWrap: "anywhere",
                                "&:focus-visible": {
                                  outline: "2px solid",
                                  outlineColor: "primary.main",
                                  outlineOffset: 2,
                                },
                              }}
                            >
                              {program.name}
                            </ButtonBase>
                            {catalogCoverageIds.has(program.program_id) ? (
                              <Chip label="Needs coach" size="small" color="warning" sx={{ mt: 0.5 }} />
                            ) : null}
                            {compactTable ? (
                              <Stack spacing={0.5} sx={{ alignItems: "start" }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {typeLabel} · {dates}
                                </Typography>
                                <Chip
                                  label={statusLabel}
                                  size="small"
                                  color={
                                    STATUS_COLORS[program.status] ?? "default"
                                  }
                                  variant="outlined"
                                />
                              </Stack>
                            ) : null}
                          </TableCell>
                          {!compactTable ? (
                            <TableCell sx={{ color: "text.secondary" }}>
                              {typeLabel}
                            </TableCell>
                          ) : null}
                          {!compactTable ? (
                            <TableCell>
                              <Chip
                                label={statusLabel}
                                size="small"
                                color={
                                  STATUS_COLORS[program.status] ?? "default"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          ) : null}
                          {!compactTable ? (
                            <TableCell
                              sx={{
                                color: "text.secondary",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {dates}
                            </TableCell>
                          ) : null}
                          <TableCell
                            align="right"
                            sx={{
                              width: compactTable ? 72 : undefined,
                              px: compactTable ? 1 : 2,
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {program.active_enrollment_count}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      )}
    </Stack>
  );
}
